import { useApolloClient } from "@apollo/client";
import { validateTemplate } from "@msys/textutils";
import {
  Modal,
  ModalOpenButton,
  RichTextEditor,
  useRunWithFlag,
} from "@msys/ui";
import { Help as HelpIcon } from "@mui/icons-material";
import {
  DialogContentText,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  ContentState,
  convertToRaw,
  type RawDraftContentState,
} from "draft-js";
import { useSnackbar } from "notistack";
import React from "react";
import { CategoriesInput } from "../../categories/CategoriesInput.js";
import { QuoteTextsAvailableReplacementsModal } from "../../quotes/modals/QuoteTextsAvailableReplacementsModal.js";
import {
  useCreateOrganisationQuoteCategoryTextsMutation,
  useModifyOrganisationQuoteCategoryTextsMutation,
} from "./QuoteCategoryTexts.generated.js";

export interface QuoteCategoryTextsValue {
  id: string;
  categoryIds: string[];
  opening?: string | null;
  closing?: string | null;
}

export interface GenericQuoteTexts {
  opening?: string | null;
  closing?: string | null;
}

type TextField = "opening" | "closing";

// The API only checks placeholder syntax, so a text written through it need not
// be editor JSON. Show such a text as plain text instead of failing to render.
function toRawContent(text: string): RawDraftContentState {
  try {
    const parsed: unknown = JSON.parse(text);
    if (isRawDraftContent(parsed)) return parsed;
  } catch {
    // Not JSON at all: shown as plain text below.
  }
  return convertToRaw(ContentState.createFromText(text));
}

function isRawDraftContent(value: unknown): value is RawDraftContentState {
  return (
    typeof value === "object" &&
    value !== null &&
    "blocks" in value &&
    Array.isArray(value.blocks) &&
    "entityMap" in value &&
    typeof value.entityMap === "object" &&
    value.entityMap !== null
  );
}

const EMPTY_TEXT = JSON.stringify(
  convertToRaw(ContentState.createFromText(""))
);

interface Props {
  handleClose: () => void;
  /** The stored set to edit, or null to start a new one for `initialCategoryIds`. */
  quoteCategoryTexts: QuoteCategoryTextsValue | null;
  initialCategoryIds: string[];
  genericTexts: GenericQuoteTexts;
  /** All stored sets; the categories of every set but this one are excluded. */
  allQuoteCategoryTexts: QuoteCategoryTextsValue[];
}

export const QuoteCategoryTextsModal = ({
  handleClose,
  quoteCategoryTexts,
  initialCategoryIds,
  genericTexts,
  allQuoteCategoryTexts,
}: Props) => {
  const { t } = useTranslate([
    "OrganisationSettings",
    "Global",
    "QuoteDetailsForm",
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [saving, runSaving] = useRunWithFlag();

  const [createTexts] = useCreateOrganisationQuoteCategoryTextsMutation({
    client,
  });
  const [modifyTexts] = useModifyOrganisationQuoteCategoryTextsMutation({
    client,
  });

  // Nothing is persisted until Save. A text of null means "not customized".
  const [categoryIds, setCategoryIds] = React.useState<string[]>(
    quoteCategoryTexts?.categoryIds ?? initialCategoryIds
  );
  const [opening, setOpening] = React.useState<string | null>(
    quoteCategoryTexts?.opening ?? null
  );
  const [closing, setClosing] = React.useState<string | null>(
    quoteCategoryTexts?.closing ?? null
  );

  const excludeCategoryIds = React.useMemo(
    () =>
      allQuoteCategoryTexts
        .filter(set => set.id !== quoteCategoryTexts?.id)
        .flatMap(set => set.categoryIds),
    [allQuoteCategoryTexts, quoteCategoryTexts?.id]
  );

  const canSave =
    quoteCategoryTexts !== null ||
    (categoryIds.length > 0 && (opening !== null || closing !== null));

  const save = async () => {
    const invalid = [opening, closing].some(
      text => text !== null && !validateTemplate(text).valid
    );
    if (invalid) {
      enqueueSnackbar(
        t("The text contains an invalid placeholder and cannot be saved.", {
          ns: "QuoteDetailsForm",
        }),
        { variant: "error" }
      );
      return;
    }
    try {
      if (quoteCategoryTexts) {
        await modifyTexts({
          variables: {
            input: { id: quoteCategoryTexts.id, categoryIds, opening, closing },
          },
        });
      } else {
        await createTexts({
          variables: {
            input: {
              categoryIds,
              ...(opening !== null ? { opening } : {}),
              ...(closing !== null ? { closing } : {}),
            },
          },
        });
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
      return;
    }
    enqueueSnackbar(t("Changes saved", { ns: "Global" }), {
      variant: "success",
    });
    handleClose();
  };

  return (
    <Modal
      title={t("Category-specific texts", { ns: "OrganisationSettings" })}
      maxWidth="sm"
      alwaysVisible
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: saving },
        },
        {
          label: t("Save", { ns: "Global" }),
          handleClick: () => {
            runSaving(save).catch(() => undefined);
          },
          buttonProps: {
            variant: "contained",
            disabled: saving || !canSave,
          },
        },
      ]}
    >
      <Stack spacing={3}>
        <DialogContentText>
          {t(
            "The texts you customize here replace the general texts for the selected categories.",
            { ns: "OrganisationSettings" }
          )}
        </DialogContentText>
        <Stack spacing={1}>
          <Typography variant="h3">
            {t("Categories", { ns: "Global" })}
          </Typography>
          {/* The heading above names the field, so no inline label. */}
          <CategoriesInput
            categoryIds={categoryIds}
            label=""
            removeChildrenIfIncluded
            addParentsWhenNotIncluded={false}
            excludeCategoryIds={excludeCategoryIds}
            onChange={categories =>
              setCategoryIds(categories.map(category => category.id))
            }
          />
        </Stack>
        <CategoryTextField
          field="opening"
          value={opening}
          genericContent={genericTexts.opening}
          onChange={setOpening}
        />
        <CategoryTextField
          field="closing"
          value={closing}
          genericContent={genericTexts.closing}
          onChange={setClosing}
        />
      </Stack>
    </Modal>
  );
};

const CategoryTextField = ({
  field,
  value,
  genericContent,
  onChange,
}: {
  field: TextField;
  value: string | null;
  genericContent: string | null | undefined;
  onChange: (value: string | null) => void;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const customize = value !== null;
  // The last custom text, so switching Customize off and on again restores it.
  const [draft, setDraft] = React.useState<string | null>(value);

  const title =
    field === "opening"
      ? t("Opening text", { ns: "OrganisationSettings" })
      : t("Closing text", { ns: "OrganisationSettings" });

  // Customize off shows the generic text, disabled; on shows the stored text.
  const rawContent = React.useMemo(() => {
    const text = value ?? genericContent;
    return text ? toRawContent(text) : undefined;
  }, [value, genericContent]);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
          <Typography variant="h3">{title}</Typography>
          <ModalOpenButton
            Modal={QuoteTextsAvailableReplacementsModal}
            modalProps={{}}
          >
            <IconButton
              size="small"
              color="secondary"
              aria-label={t("Available replacements", {
                ns: "OrganisationSettings",
              })}
            >
              <Tooltip
                title={t("Available replacements", {
                  ns: "OrganisationSettings",
                })}
              >
                <HelpIcon />
              </Tooltip>
            </IconButton>
          </ModalOpenButton>
        </Stack>
        <FormControlLabel
          control={
            <Switch
              checked={customize}
              onChange={(_event, checked) =>
                onChange(
                  checked ? (draft ?? genericContent ?? EMPTY_TEXT) : null
                )
              }
            />
          }
          label={t("Customize", { ns: "OrganisationSettings" })}
        />
      </Stack>
      <RichTextEditor
        key={customize ? "customized" : "generic"}
        content={rawContent}
        disabled={!customize}
        onChange={content => {
          if (!customize) return;
          const text = JSON.stringify(content);
          setDraft(text);
          onChange(text);
        }}
        saveButtonLabel={t("Save", { ns: "Global" })}
        cancelButtonLabel={t("Cancel", { ns: "Global" })}
      />
    </Stack>
  );
};
